import { AccessGroup, CarmResources, Program, RoleTypes } from '@carm/_core';
import { IErrorResponse } from '@carm/_utils/models';

export enum AllOrIndividualPrograms {
  all = 'all',
  individualPrograms = 'individual',
}

export enum ThirdPartyAccessGroup {
  NoChange = 'NO_CHANGE',
  NoAccess = 'NO_ACCESS',

  // this has to be confirmed, whether we have or not "THIRD_PARTY" version of EDIT and READ
  // this issue has been pending for weeks
  // for now this creates a crash if we use THIRD_PARTY_EDIT_READ and THIRD_PARTY_READ_ONLY
  // ThirdPartyEditRead = 'THIRD_PARTY_EDIT_READ',
  // ThirdPartyReadOnly = 'THIRD_PARTY_READ_ONLY',
  ThirdPartyEditRead = 'EDIT_READ',
  ThirdPartyReadOnly = 'READ_ONLY',

  ThirdPartyProgramAccountManager = 'THIRD_PARTY_PROGRAM_ACCOUNT_MANAGER',
  ThirdPartyBusinessAccountManager = 'THIRD_PARTY_BUSINESS_ACCOUNT_MANAGER',
}

export const removeThirdPartyFromGroupName = (groupTypeToOrgUnitPairs: { pairs: GroupTypeToOrgUnitPair[] }): { pairs: GroupTypeToOrgUnitPair[] } => {
  return {
   ...groupTypeToOrgUnitPairs,
   pairs: groupTypeToOrgUnitPairs?.pairs?.map(p => ({
    orgUnitUid: p?.orgUnitUid,
    groupTypeUid: p?.groupTypeUid?.replace('THIRD_PARTY_', ''),
   }))
  };
};

export const getRoleTypeFromAccessGroup = (ag: AccessGroup | ThirdPartyAccessGroup): RoleTypes => {
  let role: RoleTypes;
  switch (ag) {
    case AccessGroup.BusinessAccountManager:
      role = RoleTypes.BusinessAccountManager;
      break;
    case ThirdPartyAccessGroup.ThirdPartyBusinessAccountManager:
      role = RoleTypes.ThirdPartyBusinessAccountManager;
      break;
    case AccessGroup.ProgramAccountManager:
      role = RoleTypes.ProgramAccountManager;
      break;
    case ThirdPartyAccessGroup.ThirdPartyProgramAccountManager:
      role = RoleTypes.ThirdPartyProgramAccountManager;
      break;
    case AccessGroup.EditRead:
      role = RoleTypes.EditRead;
      break;
    case AccessGroup.ReadOnly:
      role = RoleTypes.ReadOnly;
      break;
    default:
      role = null;
  }
  return role;
};

export const getThirdPartyAccessGroupFromNative = (native: AccessGroup): ThirdPartyAccessGroup => {
  let thirdParty: ThirdPartyAccessGroup;
  switch (native) {
    case AccessGroup.NoAccess:
      thirdParty = ThirdPartyAccessGroup.NoAccess;
      break;
    case AccessGroup.NoChange:
      thirdParty = ThirdPartyAccessGroup.NoChange;
      break;
    case AccessGroup.BusinessAccountManager:
      thirdParty = ThirdPartyAccessGroup.ThirdPartyBusinessAccountManager;
      break;
    case AccessGroup.ProgramAccountManager:
      thirdParty = ThirdPartyAccessGroup.ThirdPartyProgramAccountManager;
      break;
    case AccessGroup.EditRead:
      thirdParty = ThirdPartyAccessGroup.ThirdPartyEditRead;
      break;
    case AccessGroup.ReadOnly:
      thirdParty = ThirdPartyAccessGroup.ThirdPartyReadOnly;
      break;
  }
  return thirdParty;
};

export interface IGrantAccessRequestsViewForm {
  allOrIndividualPrograms: AllOrIndividualPrograms;
  allProgramsAccessGroupControl: AccessGroup;
  programsAccessGroups: IProgramAccess[];
}

export interface IGrantAccessEmployeesViewForm {
  ownerBusinessesControl: string;
  allOrIndividualPrograms: AllOrIndividualPrograms;
  allProgramsGroups: AccessGroup;
  programsGroups: IProgramAccess[];
}

export interface IProgramAccess {
  programInfo: Program;
  accessGroup: AccessGroup;
}

export interface GroupTypeToOrgUnitPair {
  groupTypeUid: string;
  orgUnitUid: string;
}

export interface GroupTypeToOrgUnitPair {
  groupTypeUid: string;
  orgUnitUid: string;
}

export interface IApiRequestGrantAccess {
  accessRequestUids: string[];
  groupTypeToOrgUnitPairs: { pairs: GroupTypeToOrgUnitPair[] };
}

export interface IApiEmployeeGrantAccess {
  staffMemberUids: string[];
  sourceUid: string;
  groupTypeToOrgUnitPairs: { pairs: GroupTypeToOrgUnitPair[] };
}

export interface IHaveProgramPermissions {
  permissions: string[];
  program: Program;
}

export interface IHaveGrantAccessPermissions extends IErrorResponse {
  permissions: IHaveProgramPermissions[];
}

export enum GrantAccessType {
  Employee,
  AccessRequest,
  BusinessRelationsRequest,
  ClientGroups
}

export enum GrantAccessSteps {
  SelectAccess = 'view_approve_employee.step_select_access',
  Confirm = 'view_approve_employee.step_confirm',
}

export enum AccessGroupModule {
  SetupMyPortal = 'application_features.setupMyPortal.title',
  AccountsAndProfile = 'application_features.accountsAndProfiles.title',
  FinancialInformation = 'application_features.financialInformation.title',
  Payments = 'application_features.payments.title',
  Declarations = 'application_features.declarations.title',
  RulingsAppealsCompliance = 'application_features.rulings.title',
  UploadADocument = 'application_features.uploads.title'
}

export enum AccessGroupSection {
  ManageMyEmployees = 'application_features.setupMyPortal.sections.manageMyEmployees.title',
  ManageMyBusinessRelationships = 'application_features.setupMyPortal.sections.manageMyBusinessRelationships.title',
  ManageMyClientGroups = 'application_features.setupMyPortal.sections.manageMyClientGroups.title',
  ViewApiKey = 'application_features.setupMyPortal.sections.viewApiKey.title',
  EnrolInACbsaProgram = 'application_features.setupMyPortal.sections.enrollCBSAProgram.title',

  BusinessProfile = 'application_features.accountsAndProfiles.sections.businessProfile.title',
  ListOfProgramAccounts = 'application_features.accountsAndProfiles.sections.programAccounts.title',
  ProgramAccountProfile = 'application_features.accountsAndProfiles.sections.programAccountProfile.title',

  TransactionsHistory = 'application_features.financialInformation.sections.transactionHistory.title',
	ClientsTransactionHistory = 'application_features.financialInformation.sections.clientsTransactionHistory.title',
	StatementOfAccount = 'application_features.financialInformation.sections.statementAccount.title',
	BrokerSummaryReport = 'application_features.financialInformation.sections.brokerSummaryReport.title',
	CBSAInvoices = 'application_features.financialInformation.sections.cbsaInvoices.title',
	LatePaymentInterestSimulation = 'application_features.financialInformation.sections.latePaymentInterestSimulation.title',
	CollectionLetters = 'application_features.financialInformation.sections.collectionLetters.title',
	FinancialSecurity = 'application_features.financialInformation.sections.financialSecurity.title',
	ManageBonds = 'application_features.financialInformation.sections.suretyBonds.title',

  ApplyCreditsAsPayments = 'application_features.payments.sections.applyCreditsPayments.title',
  PreAuthorizedDebit = 'application_features.payments.sections.preAuthorizedDebit.title',
  PaymentArrangment = 'application_features.payments.sections.arrangements.title',

  DutiesAndTaxesCalculator = 'application_features.declarations.sections.dutiesTaxesCalculator.title',
	CreateADeclaration = 'application_features.declarations.sections.newDeclarations.title',
	ListOfSubmittedDeclarations = 'application_features.declarations.sections.listDeclarations.title',
	ManageMyTemplates = 'application_features.declarations.sections.manageTemplates.title',
	StatementsOfAdjustmentAndAppeals = 'application_features.declarations.sections.statements-of-adjustment.title',
	DutyDrawbackNonImporterOnly = 'application_features.declarations.sections.duty-drawbacks.title',
	SummaryOfDrawbackActivity = 'application_features.declarations.sections.summary-drawback-activity.title',
	RequestADeclarationReport = 'application_features.declarations.sections.requestDeclarationReport.title',
	ViewListOfRequestedDeclarationReports = 'application_features.declarations.sections.listRequestedDeclarationReport.title',

  Rulings = 'application_features.rulings.sections.rulings.title',
	Appeals = 'application_features.rulings.sections.appeals.title',
	EnforcementActions = 'application_features.rulings.sections.enforcement-actions.title',
	Compliance = 'application_features.rulings.sections.compliance-verification.title',

  UploadADocument = 'application_features.uploads.title'
}

export interface IResourcePermissionType {
  accessGroupSection: AccessGroupSection;
  resource: CarmResources;
  noChilds?: boolean;
  accessGroup?: AccessGroup;
}

export const mapAccessGroupModulesSections = new Map<AccessGroupModule, IResourcePermissionType[]>([
  [
    AccessGroupModule.SetupMyPortal, [
      { accessGroupSection: AccessGroupSection.ManageMyEmployees, resource: CarmResources.EMPLOYEE },
      { accessGroupSection: AccessGroupSection.ManageMyBusinessRelationships, resource: CarmResources.MANAGE_RELATIONSHIPS },
      { accessGroupSection: AccessGroupSection.ManageMyClientGroups, resource: CarmResources.CLIENT_GROUPS },
      { accessGroupSection: AccessGroupSection.ViewApiKey, resource: CarmResources.API },
      { accessGroupSection: AccessGroupSection.EnrolInACbsaProgram, resource: CarmResources.PROGRAM_ENROLMENT },
    ]
  ],
  [
    AccessGroupModule.AccountsAndProfile, [
      { accessGroupSection: AccessGroupSection.BusinessProfile, resource: CarmResources.BUSINESS },
      { accessGroupSection: AccessGroupSection.ListOfProgramAccounts, resource: CarmResources.PROGRAM },
      { accessGroupSection: AccessGroupSection.ProgramAccountProfile, resource: CarmResources.PROGRAM }
    ]
  ],
  [
    AccessGroupModule.FinancialInformation, [
      { accessGroupSection: AccessGroupSection.TransactionsHistory, resource: CarmResources.TRANSACTION_HISTORY },
      { accessGroupSection: AccessGroupSection.ClientsTransactionHistory, resource: CarmResources.CLIENT_TRANSACTION_HISTORY },
      { accessGroupSection: AccessGroupSection.StatementOfAccount, resource: CarmResources.FINANCIAL_STATEMENTS },
      { accessGroupSection: AccessGroupSection.BrokerSummaryReport, resource: CarmResources.BROKER_SUMMARY },
      { accessGroupSection: AccessGroupSection.CBSAInvoices, resource: CarmResources.FINANCIAL_STATEMENTS },
      { accessGroupSection: AccessGroupSection.LatePaymentInterestSimulation, resource: CarmResources.INTEREST_SIMULATION },
      { accessGroupSection: AccessGroupSection.CollectionLetters, resource: CarmResources.COLLECTIONS },
      { accessGroupSection: AccessGroupSection.FinancialSecurity, resource: CarmResources.BONDS_LIST },
      { accessGroupSection: AccessGroupSection.ManageBonds, resource: CarmResources.SURETY_PROVIDER },
    ]
  ],
  [
    AccessGroupModule.Payments, [
      { accessGroupSection: AccessGroupSection.ApplyCreditsAsPayments, resource: CarmResources.APPLY_CREDITS },
      { accessGroupSection: AccessGroupSection.PreAuthorizedDebit, resource: CarmResources.PAYMENTS },
      { accessGroupSection: AccessGroupSection.PaymentArrangment, resource: CarmResources.PAYMENTS }
    ]
  ],
  [
    AccessGroupModule.Declarations, [
      { accessGroupSection: AccessGroupSection.DutiesAndTaxesCalculator, resource: CarmResources.DUTY_AND_TAX },
      { accessGroupSection: AccessGroupSection.CreateADeclaration, resource: CarmResources.DECLARATIONS },
      { accessGroupSection: AccessGroupSection.ListOfSubmittedDeclarations, resource: CarmResources.DECLARATIONS },
      { accessGroupSection: AccessGroupSection.ManageMyTemplates, resource: CarmResources.DECLARATIONS },
      { accessGroupSection: AccessGroupSection.StatementsOfAdjustmentAndAppeals, resource: CarmResources.DECLARATIONS },
      { accessGroupSection: AccessGroupSection.DutyDrawbackNonImporterOnly, resource: CarmResources.DECLARATIONS_DDB_NONIMP },
      { accessGroupSection: AccessGroupSection.SummaryOfDrawbackActivity, resource: CarmResources.DECLARATIONS },
      { accessGroupSection: AccessGroupSection.RequestADeclarationReport, resource: CarmResources.DECLARATIONS },
      { accessGroupSection: AccessGroupSection.ViewListOfRequestedDeclarationReports, resource: CarmResources.DECLARATIONS },
    ]
  ],
  [
    AccessGroupModule.RulingsAppealsCompliance, [
      { accessGroupSection: AccessGroupSection.Rulings, resource: CarmResources.RULINGS },
      { accessGroupSection: AccessGroupSection.Appeals, resource: CarmResources.APPEALS },
      { accessGroupSection: AccessGroupSection.EnforcementActions, resource: CarmResources.ENFORCEMENT_ACTIONS },
      { accessGroupSection: AccessGroupSection.Compliance, resource: CarmResources.COMPLIANCE },
    ]
  ],
  [
    AccessGroupModule.UploadADocument, [
      { accessGroupSection: AccessGroupSection.UploadADocument, resource: CarmResources.DOCUMENTS, noChilds: true }
    ]
  ]
]);